import React from "react";
import {
    Anchor,
    Brain,
    HeartPulse,
    Sparkles,
    Moon,
    Activity,
    Target,
    BatteryCharging,
    Users,
    Wind,
    Compass,
} from "lucide-react";
import {
    LOGO_URL,
    PHONE_DISPLAY,
    INSTAGRAM,
    waUrl,
} from "../lib/constants";

/**
 * 2-page A4 YACHT brochure for Elite Rentals Dubai × The Reset Sanctuary.
 * Page 1: Hero · Problem · Solution · From→To · 4 Benefits
 * Page 2: Five Outcomes · Equipment Toolkit · Tatva K · Onboard Formats · CTA & Contact
 * View at /brochure-yacht. Print → Save as PDF.
 * Compliant wording (no banned terms outside Berklee credential).
 */

const GOLD = "#c9a227";
const GOLD_LINE = "rgba(201,162,39,0.25)";
const GOLD_LINE_SOFT = "rgba(201,162,39,0.15)";
const YACHT_EMAIL = "tatvak@theresetsanctuary.org";

const PageFrame = ({ children, testid }) => (
    <article
        className="brochure-page brochure-yacht-page"
        data-testid={testid}
        style={{
            width: 794,
            height: 1123,
            maxWidth: "100%",
            margin: "0 auto 32px",
            backgroundColor: "#0a0a0a",
            color: "#bbb",
            boxShadow:
                "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,162,39,0.18)",
            padding: "40px 44px 32px",
            boxSizing: "border-box",
            position: "relative",
            fontFamily: "Inter, sans-serif",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}
    >
        <div
            style={{
                position: "absolute",
                inset: 18,
                border: "1px solid rgba(201,162,39,0.22)",
                pointerEvents: "none",
            }}
        />
        {children}
    </article>
);

const PageHeader = ({ kicker }) => (
    <header
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            marginBottom: 14,
        }}
    >
        <img src={LOGO_URL} alt="The Reset Sanctuary" style={{ height: 58 }} />
        <div style={{ textAlign: "right" }}>
            <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", margin: 0, fontWeight: 500 }}>
                {kicker}
            </p>
            <p style={{ color: "#888", fontSize: 8.5, letterSpacing: "0.2em", textTransform: "uppercase", margin: "4px 0 0" }}>
                Marina · Palm · Burj Al Arab · Atlantis
            </p>
        </div>
    </header>
);

const PageFooter = ({ pageNum, tagline }) => (
    <div
        style={{
            marginTop: "auto",
            paddingTop: 14,
            borderTop: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            zIndex: 1,
            fontSize: 8,
            color: "#666",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            gap: 12,
        }}
    >
        <span>theresetsanctuary.org</span>
        <span style={{ color: GOLD, textAlign: "center", flex: 1 }}>{tagline}</span>
        <span>Page {pageNum} / 2</span>
    </div>
);

const SectionTitle = ({ kicker, title, color = "#fff" }) => (
    <div style={{ marginBottom: 10 }}>
        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500 }}>
            {kicker}
        </p>
        <h2 className="font-serif" style={{ color, fontSize: 20, fontWeight: 500, margin: 0, lineHeight: 1.1, letterSpacing: "-0.005em" }}>
            {title}
        </h2>
    </div>
);

const BenefitTile = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "14px 13px",
            border: `1px solid ${GOLD_LINE}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            backgroundColor: "rgba(201,162,39,0.025)",
        }}
    >
        <Icon size={24} color={GOLD} strokeWidth={1.4} />
        <p style={{ color: "#fff", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", margin: 0, fontWeight: 500, lineHeight: 1.25 }}>
            {label}
        </p>
        <p style={{ color: "#aaa", fontSize: 8.5, margin: 0, lineHeight: 1.5, fontWeight: 300 }}>
            {body}
        </p>
    </div>
);

const SmallBenefit = ({ Icon, label, body, testid }) => (
    <div
        data-testid={testid}
        style={{
            padding: "12px 10px",
            border: `1px solid ${GOLD_LINE_SOFT}`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
            alignItems: "center",
            textAlign: "center",
        }}
    >
        <Icon size={22} color={GOLD} strokeWidth={1.4} />
        <p style={{ color: "#fff", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", margin: 0, fontWeight: 500, lineHeight: 1.25 }}>
            {label}
        </p>
        <p style={{ color: "#aaa", fontSize: 8.5, margin: 0, lineHeight: 1.5, fontWeight: 300 }}>
            {body}
        </p>
    </div>
);

const Pillar = ({ title, body }) => (
    <div style={{ padding: "12px 12px", borderTop: `1px solid ${GOLD_LINE}` }}>
        <p style={{ color: GOLD, fontSize: 8.5, letterSpacing: "0.22em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500, lineHeight: 1.3 }}>
            {title}
        </p>
        <p style={{ color: "#bbb", fontSize: 9, lineHeight: 1.55, margin: 0, fontWeight: 300 }}>
            {body}
        </p>
    </div>
);

const FormatCard = ({ tier, duration, capacity, headline, items, featured, testid }) => (
    <div
        style={{
            padding: "16px 14px 18px",
            border: featured ? `1px solid ${GOLD}` : `1px solid ${GOLD_LINE}`,
            backgroundColor: featured ? "rgba(201,162,39,0.04)" : "transparent",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 7,
            height: "100%",
        }}
        data-testid={testid}
    >
        {featured && (
            <span style={{ position: "absolute", top: -10, left: 14, backgroundColor: GOLD, color: "#0a0a0a", fontSize: 8, letterSpacing: "0.28em", textTransform: "uppercase", padding: "3px 10px", fontWeight: 600 }}>
                Signature
            </span>
        )}
        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.32em", textTransform: "uppercase", margin: 0, fontWeight: 500 }}>
            {tier}
        </p>
        <p className="font-serif" style={{ color: "#fff", fontSize: 18, fontWeight: 400, margin: 0, lineHeight: 1.15, letterSpacing: "-0.005em" }}>
            {duration}
            <span style={{ fontSize: 10, color: "#888", fontFamily: "Inter, sans-serif", letterSpacing: "0.18em", textTransform: "uppercase", marginLeft: 6 }}>
                · {capacity}
            </span>
        </p>
        <p style={{ color: "#bbb", fontSize: 9.5, margin: 0, lineHeight: 1.5, fontWeight: 300 }}>
            {headline}
        </p>
        <div style={{ borderTop: `1px solid ${GOLD_LINE_SOFT}`, marginTop: 2, paddingTop: 8 }}>
            {items.map((it, i) => (
                <p key={i} style={{ color: "#fff", fontSize: 9.5, margin: "3px 0", lineHeight: 1.4 }}>
                    · {it}
                </p>
            ))}
        </div>
    </div>
);

const BrochureYacht = () => {
    const yachtWa = waUrl(
        "Hi, I'd like to discuss the Elite Rentals × Reset Sanctuary onboard recovery experience.",
    );

    return (
        <main
            data-testid="brochure-yacht-page"
            style={{
                backgroundColor: "#0a0a0a",
                minHeight: "100vh",
                padding: "40px 16px 80px",
                color: "#bbb",
            }}
        >
            {/* Screen-only print button */}
            <div
                className="brochure-screen-only"
                style={{
                    maxWidth: 794,
                    margin: "0 auto 24px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 16,
                    flexWrap: "wrap",
                }}
            >
                <p style={{ color: "#888", fontSize: 12, letterSpacing: "0.15em", textTransform: "uppercase", margin: 0 }}>
                    Elite Rentals × TRS — A4 · 2 pages
                </p>
                <button
                    onClick={() => window.print()}
                    className="btn-gold-solid"
                    data-testid="brochure-yacht-print-btn"
                    style={{ cursor: "pointer", fontFamily: "Inter, sans-serif" }}
                >
                    Save as PDF / Print
                </button>
            </div>

            <style>{`@media print { @page { size: A4; margin: 0; } body, main { background: #fff !important; padding: 0 !important; } header[data-testid="main-navigation"], a[data-testid="floating-whatsapp-btn"], footer, .brochure-screen-only { display: none !important; } .brochure-page { box-shadow: none !important; margin: 0 !important; page-break-after: always; } .brochure-page:last-child { page-break-after: auto; } }`}</style>

            {/* ============== PAGE 1 ============== */}
            <PageFrame testid="brochure-yacht-a4">
                <PageHeader kicker="Sound Recovery at Sea · Elite Rentals × TRS" />

                {/* HERO HEADLINE */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12 }}>
                    <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", margin: "0 0 8px", fontWeight: 500 }}>
                        The World&apos;s First Precision Frequency Recovery Experience at Sea
                    </p>
                    <h1
                        className="font-serif"
                        style={{
                            color: "#fff",
                            fontSize: 32,
                            fontWeight: 400,
                            lineHeight: 1.04,
                            letterSpacing: "-0.025em",
                            margin: "0 0 8px",
                            textTransform: "uppercase",
                        }}
                    >
                        Real Wealth. Real Stress.{" "}
                        <span style={{ color: GOLD }}>Real Recovery.</span>
                    </h1>
                </section>

                {/* HERO IMAGE */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        width: "100%",
                        aspectRatio: "16 / 9",
                        overflow: "hidden",
                        border: `1px solid ${GOLD_LINE}`,
                    }}
                    data-testid="yacht-hero-image"
                >
                    <img
                        src="/brochure-assets/yacht-hero.jpg"
                        alt="Luxury yacht in Dubai Marina"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center 50%",
                            display: "block",
                        }}
                    />
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(10,10,10,0) 55%, rgba(10,10,10,0.7) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                </section>

                {/* PROBLEM + SOLUTION */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                    }}
                >
                    <div data-testid="yacht-problem">
                        <SectionTitle
                            kicker="The Problem"
                            title="The nervous system never docks."
                        />
                        <p style={{ color: "#bbb", fontSize: 10, lineHeight: 1.6, margin: 0, fontWeight: 300 }}>
                            Boardrooms, jets, screens, deal-rooms. Founders, CEOs and athletes carry sympathetic stress 24/7 — and a champagne charter alone does not move the needle on sleep, focus or recovery.
                        </p>
                    </div>
                    <div data-testid="yacht-solution">
                        <SectionTitle
                            kicker="The Solution"
                            title="Precision Frequency Recovery."
                            color={GOLD}
                        />
                        <p style={{ color: "#bbb", fontSize: 10, lineHeight: 1.6, margin: 0, fontWeight: 300 }}>
                            90 minutes onboard. An engineered protocol that downshifts the autonomic nervous system from sympathetic stress into deep parasympathetic recovery — measurable, repeatable, at sea.
                        </p>
                    </div>
                </section>

                {/* FROM → TO STRIP */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 14,
                        padding: "12px 18px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "1fr auto 1fr",
                        gap: 18,
                        alignItems: "center",
                    }}
                    data-testid="yacht-shift-strip"
                >
                    <div style={{ textAlign: "right" }}>
                        <p style={{ color: "#777", fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px" }}>From</p>
                        <p
                            style={{
                                color: "#bbb",
                                fontSize: 10,
                                letterSpacing: "0.18em",
                                textTransform: "uppercase",
                                margin: 0,
                                lineHeight: 1.6,
                                textDecoration: "line-through",
                                textDecorationColor: "rgba(201,162,39,0.5)",
                            }}
                        >
                            Pressure · Jet-lag · Sleep Debt · Burnout
                        </p>
                    </div>
                    <span style={{ color: GOLD, fontSize: 16, fontWeight: 300 }}>→</span>
                    <div>
                        <p style={{ color: GOLD, fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px" }}>To</p>
                        <p style={{ color: "#fff", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", margin: 0, lineHeight: 1.6, fontWeight: 500 }}>
                            Recovery · Clarity · Performance · Presence
                        </p>
                    </div>
                </section>

                {/* 4 BENEFITS */}
                <section
                    style={{ position: "relative", zIndex: 1, marginBottom: 12 }}
                    data-testid="yacht-benefits"
                >
                    <SectionTitle kicker="The Benefits" title="What changes onboard, in 90 minutes." />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                        <BenefitTile
                            Icon={Brain}
                            label="Reset the Nervous System"
                            body="Engineered frequencies guide the body from sympathetic stress into parasympathetic recovery."
                            testid="yacht-benefit-reset"
                        />
                        <BenefitTile
                            Icon={Moon}
                            label="Recover Sleep, Reclaim Energy"
                            body="Calms an overactive mind so deeper sleep, sharper mornings and full-day energy return."
                            testid="yacht-benefit-sleep"
                        />
                        <BenefitTile
                            Icon={Target}
                            label="Sharpen Focus & Decisions"
                            body="Brainwave entrainment moves you from Beta into Alpha and Theta — where clarity lives."
                            testid="yacht-benefit-focus"
                        />
                        <BenefitTile
                            Icon={HeartPulse}
                            label="Restore the Body"
                            body="PEMF and vibroacoustic protocols support cellular recovery, circulation and mobility."
                            testid="yacht-benefit-body"
                        />
                    </div>
                </section>

                <PageFooter pageNum={1} tagline="Recover · Realign · Perform" />
            </PageFrame>

            {/* ============== PAGE 2 ============== */}
            <PageFrame testid="brochure-yacht-a4-page2">
                <PageHeader kicker="Science · Formats · Contact" />

                {/* FIVE OUTCOMES */}
                <section
                    style={{ position: "relative", zIndex: 1, marginBottom: 14 }}
                    data-testid="yacht-success"
                >
                    <SectionTitle
                        kicker="Sound Recovery Supports the Charter"
                        title="Five outcomes guests feel at sea."
                    />
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
                        <SmallBenefit Icon={Wind} label="Deep Calm" body="Slow the breath, lower cortisol and arrive in the present." testid="yacht-out-calm" />
                        <SmallBenefit Icon={Moon} label="Better Sleep" body="Train the system to fall asleep faster and stay there." testid="yacht-out-sleep" />
                        <SmallBenefit Icon={Activity} label="Cellular Recovery" body="PEMF supports inflammation, circulation and mobility." testid="yacht-out-cell" />
                        <SmallBenefit Icon={Sparkles} label="Mental Clarity" body="Brainwave entrainment for sharper focus and decisions." testid="yacht-out-clarity" />
                        <SmallBenefit Icon={Compass} label="Presence" body="Reconnect with family, partners and the moment at sea." testid="yacht-out-present" />
                    </div>
                </section>

                {/* EQUIPMENT */}
                <section
                    style={{ position: "relative", zIndex: 1, marginBottom: 12 }}
                    data-testid="yacht-equipment"
                >
                    <SectionTitle kicker="Equipment & Frequencies" title="The onboard protocol toolkit." />
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 }}>
                        {[
                            {
                                src: "/brochure-assets/pemf-mat.jpg",
                                label: "PEMF Mat",
                                caption: "Pulsed electromagnetic stimulation for cellular recovery — used by NASA and Olympic athletes.",
                                testid: "yacht-equipment-pemf",
                            },
                            {
                                src: "/brochure-assets/tuning-forks.jpg",
                                label: "Tuning Forks",
                                caption: "Calibrated to 432 · 528 · 174 · 741 Hz — the recovery and regulation bands.",
                                testid: "yacht-equipment-forks",
                            },
                        ].map((item) => (
                            <div
                                key={item.testid}
                                data-testid={item.testid}
                                style={{ border: `1px solid ${GOLD_LINE}`, overflow: "hidden", display: "flex", flexDirection: "column" }}
                            >
                                <div style={{ width: "100%", aspectRatio: "16 / 9", overflow: "hidden" }}>
                                    <img src={item.src} alt={item.label} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                </div>
                                <div style={{ padding: "8px 12px 10px" }}>
                                    <p style={{ color: GOLD, fontSize: 8.5, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 3px", fontWeight: 500 }}>
                                        {item.label}
                                    </p>
                                    <p style={{ color: "#bbb", fontSize: 9, lineHeight: 1.45, margin: 0, fontWeight: 300 }}>
                                        {item.caption}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "2px 16px" }}>
                        <Pillar
                            title="Precision Frequency Generator"
                            body="Calibrated brainwave entrainment — Beta → Alpha → Theta — for measurable autonomic shift."
                        />
                        <Pillar
                            title="Singing Bowls & Vibroacoustic"
                            body="Harmonic resonance and engineered low-frequency vibration through the body."
                        />
                    </div>
                </section>

                {/* PRACTITIONER */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 12,
                        padding: "12px 14px",
                        border: `1px solid ${GOLD_LINE}`,
                        display: "grid",
                        gridTemplateColumns: "62px 1fr",
                        gap: 12,
                        alignItems: "center",
                    }}
                    data-testid="yacht-practitioner"
                >
                    <img
                        src="/brochure-assets/tatva.png"
                        alt="Tatva K"
                        style={{
                            width: 62,
                            height: 62,
                            objectFit: "cover",
                            objectPosition: "center 25%",
                            borderRadius: "50%",
                            border: `1px solid rgba(201,162,39,0.4)`,
                            display: "block",
                        }}
                    />
                    <div>
                        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.32em", textTransform: "uppercase", margin: "0 0 4px", fontWeight: 500 }}>
                            Led by Tatva K · Founder, The Reset Sanctuary
                        </p>
                        <p style={{ color: "#fff", fontSize: 9, lineHeight: 1.5, margin: 0, fontWeight: 300 }}>
                            TEDx Speaker · 150M+ Aggregate Streams · Featured by BBC & Rolling Stone · Berklee College of Music — Music Therapy Masterclass · ISTA Certified · IPHM Certified · Reiki Grandmaster · Featured Practitioner on Insight Timer
                        </p>
                    </div>
                </section>

                {/* ONBOARD FORMATS */}
                <section style={{ position: "relative", zIndex: 1, marginBottom: 12 }} data-testid="yacht-formats">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 }}>
                        <h2 className="font-serif" style={{ color: "#fff", fontSize: 18, fontWeight: 500, margin: 0, letterSpacing: "0.01em" }}>
                            Onboard Formats
                        </h2>
                        <p style={{ color: "#888", fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", margin: 0 }}>
                            Executive · Sunset · Charter
                        </p>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
                        <FormatCard
                            tier="Executive Reset"
                            duration="90 min"
                            capacity="1 – 4 guests"
                            headline="The signature onboard protocol for founders, CEOs and athletes."
                            items={["Full PEMF + frequency session", "Personal intake", "Onboard, in-cabin or upper deck", "Post-session debrief"]}
                            testid="yacht-format-exec"
                            featured
                        />
                        <FormatCard
                            tier="Sunset Voyage"
                            duration="2 hrs"
                            capacity="up to 10 guests"
                            headline="Group sound bath, binaural immersion and meditation at golden hour."
                            items={["Group sound bath", "Breathwork at sea", "Champagne · healthy canapés", "Marina, Palm or Burj route"]}
                            testid="yacht-format-sunset"
                        />
                        <FormatCard
                            tier="Private Charter"
                            duration="3 – 4 hrs"
                            capacity="Family · Team · VIP"
                            headline="A bespoke recovery experience for UHNW families, retreats and brand launches."
                            items={["Multi-format experience", "Family · team · group cohorts", "Custom route & catering", "Founder-led briefing"]}
                            testid="yacht-format-charter"
                        />
                    </div>
                    <p style={{ color: "#777", fontSize: 8.5, margin: "10px 0 0", letterSpacing: "0.04em" }}>
                        Packages and per-charter pricing curated jointly with Elite Rentals Dubai. Tailored to fleet, route and guest profile.
                    </p>
                </section>

                {/* CTA + CONTACT */}
                <section
                    style={{
                        position: "relative",
                        zIndex: 1,
                        display: "grid",
                        gridTemplateColumns: "1.1fr 1fr",
                        gap: 18,
                        marginBottom: 8,
                    }}
                >
                    <div>
                        <h2 className="font-serif" style={{ color: "#fff", fontSize: 20, fontWeight: 400, margin: "0 0 6px", letterSpacing: "-0.01em" }}>
                            Recover at Sea.{" "}
                            <span style={{ color: GOLD }}>Realign Onboard.</span>
                        </h2>
                        <p style={{ color: "#bbb", fontSize: 10, lineHeight: 1.55, margin: 0, maxWidth: 380, fontWeight: 300 }}>
                            A co-branded luxury proposition by Elite Rentals Dubai and The Reset Sanctuary. Concierge booking. Same-day confirmation. Curated for the people who move between worlds.
                        </p>
                        <div style={{ marginTop: 12 }}>
                            <p style={{ color: GOLD, fontSize: 8, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500 }}>
                                Ideal For
                            </p>
                            <p style={{ color: "#bbb", fontSize: 9.5, lineHeight: 1.55, margin: 0, fontWeight: 300 }}>
                                Corporate retreats · executive offsites · brand launches · VIP birthdays · pre-wedding experiences · family offices · luxury concierge partners.
                            </p>
                        </div>
                    </div>
                    <div style={{ textAlign: "right", fontFamily: "Inter, sans-serif" }}>
                        <p style={{ color: GOLD, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", margin: "0 0 6px", fontWeight: 500 }}>
                            Reserve via WhatsApp
                        </p>
                        <p className="font-serif" style={{ color: "#fff", fontSize: 18, fontWeight: 500, margin: "0 0 6px", letterSpacing: "0.02em" }}>
                            {PHONE_DISPLAY}
                        </p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 2px" }}>{YACHT_EMAIL}</p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 2px" }}>theresetsanctuary.org</p>
                        <p style={{ color: "#bbb", fontSize: 10, margin: "0 0 10px" }}>Instagram {INSTAGRAM}</p>
                        <a
                            href={yachtWa}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="yacht-cta"
                            className="brochure-screen-only"
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                color: "#0a0a0a",
                                backgroundColor: GOLD,
                                padding: "10px 18px",
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                fontWeight: 600,
                                textDecoration: "none",
                            }}
                        >
                            <Anchor size={14} strokeWidth={2} /> Reserve Onboard
                        </a>
                    </div>
                </section>

                {/* DISCLAIMER */}
                <p
                    style={{
                        color: "#666",
                        fontSize: 7,
                        lineHeight: 1.5,
                        textAlign: "center",
                        margin: "0 0 10px",
                        position: "relative",
                        zIndex: 1,
                        paddingTop: 8,
                        borderTop: `1px solid ${GOLD_LINE_SOFT}`,
                    }}
                >
                    The Reset Sanctuary offers wellness and recovery sessions through precision sound and frequency work. We do not diagnose, treat, or cure medical conditions. Our services are complementary wellness practices, not medical care. Please consult a licensed healthcare provider for medical concerns. © 2026 Tatva K.
                </p>

                <PageFooter pageNum={2} tagline="Engineered Recovery · Delivered at Sea" />
            </PageFrame>
        </main>
    );
};

export default BrochureYacht;
